import type { RuntimeContext } from 'hypertube-nodejs-sdk'

import { Runtime } from '@/core/enums/runtime.enum'

import { buildWsUrl } from '../buildWsUrl'
import { normalizeRuntimeName } from '../runtime/normalizeRuntimeName'

import { RUNTIME_CONNECTION_ACCESSOR_MAP } from './const/runtimeConnectionAccessorMap.const'
import { RUNTIME_MAP } from './const/runtimeMap.const'

type HypertubeModule = typeof import('hypertube-nodejs-sdk')
type HypertubeConnection = ReturnType<HypertubeModule['RuntimeBridge']['withConfig']>

export const getContext = async ({
  gatewayPath,
  gatewayPort,
  isSecured,
  runtimeName,
}: {
  gatewayPath: string
  gatewayPort?: string
  isSecured: boolean
  runtimeName: Runtime
}): Promise<RuntimeContext> => {
  if (typeof window === 'undefined') {
    throw new Error('getContext can only be called in the browser.')
  }

  if (window.location.protocol === 'https:' && !isSecured) {
    throw new Error(
      'Cannot establish WebSocket connection: the app is loaded over HTTPS, but the gateway is configured to use ws://. Use a secure wss:// gateway or open the app over http:// for local development.'
    )
  }

  const { loadHypertube } = await import('@common/core/lib/hypertube')
  const { RuntimeBridge } = (await loadHypertube()) as HypertubeModule

  const host = buildWsUrl(gatewayPath, gatewayPort, isSecured)
  const normalizedRuntime = normalizeRuntimeName(runtimeName)
  const runtimeKey = RUNTIME_MAP[normalizedRuntime]
  if (!runtimeKey) {
    throw new Error(`Unsupported runtime: ${runtimeName}`)
  }

  const connection = RuntimeBridge.withConfig({
    runtimes: {
      [runtimeKey]: {
        name: 'default',
        channel: { type: 'webSocket', host },
      },
    },
  })

  const getRuntimeConnection = RUNTIME_CONNECTION_ACCESSOR_MAP[normalizedRuntime]
  if (!getRuntimeConnection) {
    throw new Error(`Unsupported runtime key: ${runtimeKey}`)
  }

  return getRuntimeConnection(connection as HypertubeConnection)
}
